// components/PreviewElement.tsx
import React from 'react';
import styled from 'styled-components';
import { Element, TempSettings } from '../../types/types';
import { useTheme } from '../../middleware/ThemeContext';
import { font } from '../../utils/themes';
import StyledText from '../StyledComponents/StyledText';
import FormRenderComponent from './FormRender/FormRenderComponent';
import PublicDirectory from './PublicDirectory';
import SummaryCardComponent from './Summary';
import { getElementClassFromProperties } from '../../utils/pageBuilderElementClass';
import { isYoutubeUrl, getYoutubeEmbedUrl } from '../../utils/youtubeHelper';

const ElementWrapper = styled.div`
  padding: 15px 0px;
`;

const ButtonElement = styled.button<{
  $bgColor: string;
  $textColor: string;
  $hoverColor: string;
  $padding: string;
}>`
  background: ${({ $bgColor }) => $bgColor};
  color: ${({ $textColor }) => $textColor};
  border: none;
  padding: ${({ $padding }) => $padding};
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ $hoverColor }) => $hoverColor};
  }
`;

const ButtonWrapper = styled.div<{ $alignment?: string }>`
  display: flex;
  justify-content: ${({ $alignment }) => {
    if ($alignment === 'center') {
      return 'center';
    }
    if ($alignment === 'right') {
      return 'flex-end';
    }
    return 'flex-start';
  }};
`;

const ButtonLink = styled.a`
  display: inline-flex;
  text-decoration: none;
`;

// Lazy load SearchQueryRenderer
const LazySearchQueryRenderer = React.lazy(() =>
  import('./SearchQueryRenderer').then(module => ({
    default: module.SearchQueryRenderer
  }))
);

const LazyPublicDirectory = React.lazy(async () => ({
  default: PublicDirectory
}));

interface PreviewElementProps {
  element: Element;
  pendingChanges: TempSettings;
  primaryFont?: string;
}

const PreviewElement: React.FC<PreviewElementProps> = ({
  element,
  pendingChanges,
  primaryFont
}) => {
  const { theme } = useTheme();

  // Merge element with pending changes
  const mergedElement: any = {
    ...element,
    ...element.properties,
    ...pendingChanges
  };

  const uniqueElementClass =
    typeof mergedElement?.elementClass === 'string' && mergedElement.elementClass.trim()
      ? mergedElement.elementClass.trim()
      : getElementClassFromProperties(element);
  const typeClass = `${element.type}-element`;

  const renderElementContent = () => {
    if (element.type === 'heading') {
      const headingContent = mergedElement?.htmlContent || mergedElement?.content || element?.content || 'Heading Goes Here';
      const headingTag = mergedElement?.heading?.toLowerCase() || 'h1';
      return (
        React.createElement(headingTag, {
          className: `${typeClass} ${uniqueElementClass}`,
          style: {
            fontSize: mergedElement?.fontSize || '48px',
            fontWeight: mergedElement?.fontWeight === "regular" ? 400 : mergedElement?.fontWeight === 'medium' ? 500 : 700,
            textAlign: (mergedElement?.textAlignment || 'left') as any,
            fontFamily: primaryFont || font.raleway,
            color: mergedElement?.color || '#1a3a5c'
          },
          'data-element-id': element.id,
          'data-element-type': element.type
        }, headingContent)
      );
    }

    if (element.type === 'paragraph') {
      const paragraphContent = mergedElement?.htmlContent || mergedElement?.paragraphContent || element?.content || 'This is a paragraph. Add your content here.';
      const isHtml = /<[^>]*>/.test(paragraphContent);
      return isHtml ? (
          <div
            className={`${typeClass} ${uniqueElementClass} ql-editor`}
            data-element-id={element.id}
            data-element-type={element.type}
            style={{
              // textAlign: mergedElement?.paragraphTextAlignment || 'left',
              pointerEvents: 'none',
              fontFamily: primaryFont || font.raleway,
              color: mergedElement?.paragraphColor || '#000000',
              // fontWeight: mergedElement?.paragraphFontWeight === "regular" ? 400 : mergedElement?.paragraphFontWeight === 'medium' ? 500 : 700,
              fontSize: mergedElement?.paragraphFontSize
                ? String(mergedElement.paragraphFontSize).includes('px')
                  ? mergedElement.paragraphFontSize
                  : `${mergedElement.paragraphFontSize}px`
                : '14px'
            }}
            dangerouslySetInnerHTML={{ __html: paragraphContent }}
          />
        ) : (
        <p
          className={`${typeClass} ${uniqueElementClass}`}
          data-element-id={element.id}
          data-element-type={element.type}
          style={{
            textAlign: mergedElement?.paragraphTextAlignment || 'left',
            fontFamily: primaryFont || font.raleway,
            color: mergedElement?.paragraphColor || '#000000',
            fontWeight: mergedElement?.paragraphFontWeight === "regular" ? 400 : mergedElement?.paragraphFontWeight === 'medium' ? 500 : 700,
            fontSize: mergedElement?.paragraphFontSize || '14px'
          }}
        >
          {paragraphContent}
        </p>
      );
    }

    if (element.type === 'separator') {
      return (
        <hr
          style={{
            border: 'none',
            borderTop: `${mergedElement?.separatorHeight || '1px'} ${mergedElement?.separatorType || 'solid'} ${mergedElement?.separatorColor || '#e0e0e0'}`,
            margin: '10px 0'
          }}
        />
      );
    }

    if (element.type === 'media') {
      const mediaType = mergedElement?.mediaType || 'Image';
      const mediaTypePrefix = mediaType.toLowerCase();

      const mediaUrl = mergedElement?.signedUrl ||
        mergedElement?.[`${mediaTypePrefix}Preview`] ||
        mergedElement?.[`${mediaTypePrefix}ExternalURL`] ||
        mergedElement?.[`${mediaTypePrefix}Url`] ||
        mergedElement?.[`${mediaTypePrefix}URL`];

      const textAlignment = mergedElement?.textAlignment || 'center';
      const caption = mergedElement?.content || '';
      const linkURL = mergedElement?.linkURL;
      const openNewTab = mergedElement?.openNewTab;

      const mediaElement = mediaUrl ? (
        mediaType === 'Image' ? (
          <img
            src={mediaUrl}
            alt={caption || 'Media'}
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '6px',
              display: 'block',
              margin: textAlignment === 'center' ? '0 auto' : textAlignment === 'right' ? '0 0 0 auto' : '0',
              cursor: linkURL ? 'pointer' : 'default'
            }}
          />
        ) : isYoutubeUrl(mediaUrl) ? (
          <iframe
            key={mediaUrl}
            width="560px"
            height="300px"
            src={getYoutubeEmbedUrl(mediaUrl) || mediaUrl}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              maxWidth: '100%',
              borderRadius: '6px',
              display: 'block',
              margin: textAlignment === 'center' ? '0 auto' : textAlignment === 'right' ? '0 0 0 auto' : '0',
              cursor: linkURL ? 'pointer' : 'default'
            }}
          />
        ) : (
          <video
            key={mediaUrl}
            controls
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              borderRadius: '6px',
              display: 'block',
              margin: textAlignment === 'center' ? '0 auto' : textAlignment === 'right' ? '0 0 0 auto' : '0',
              cursor: linkURL ? 'pointer' : 'default'
            }}
          >
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )
      ) : (
        <div style={{
          background: '#f8f9fa',
          padding: '40px',
          textAlign: 'center',
          borderRadius: '6px',
          color: '#6c757d'
        }}>
          {caption || element.content || `No ${mediaType} uploaded`}
        </div>
      );

      return (
        <div style={{
          textAlign: textAlignment as any,
          borderRadius: '6px'
        }}>
          {linkURL && mediaUrl ? (
            <a
              href={linkURL}
              target={openNewTab ? '_blank' : undefined}
              rel={openNewTab ? 'noopener noreferrer' : undefined}
              style={{ textDecoration: 'none', display: 'inline-block', width: textAlignment === 'center' ? '100%' : 'auto' }}
            >
              {mediaElement}
            </a>
          ) : (
            mediaElement
          )}
        </div>
      );
    }

    if (element.type === 'form') {
      if (mergedElement?.formId) {
        return (
          <FormRenderComponent
            formId={mergedElement.formId}
            formName={mergedElement.formName}
          />
        );
      }
      return (
        <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '6px', textAlign: 'center' }}>
          <StyledText fontSize="14px" color="#666">
            No form selected. Configure form in settings.
          </StyledText>
        </div>
      );
    }

    if (element.type === 'button') {
      const buttonPadding = mergedElement?.buttonSize === 'small'
        ? '8px 20px'
        : mergedElement?.buttonSize === 'large'
          ? '16px 40px'
          : '12px 30px';
      const buttonContent = mergedElement?.content || element.content;
      const buttonAlignment = mergedElement?.buttonAlignment || 'left';
      const buttonBgColor = mergedElement?.buttonColor || '#0050ff';
      const buttonTextColor = mergedElement?.buttonTextColor || '#DDEBFF';
      const buttonHoverColor = mergedElement?.buttonHoverColor || '#003FC7';
      const buttonLink = mergedElement?.buttonLink;
      const openNewTab = mergedElement?.openNewTab;

      const buttonNode = (
        <ButtonElement
          $bgColor={buttonBgColor}
          $textColor={buttonTextColor}
          $hoverColor={buttonHoverColor}
          $padding={buttonPadding}
        >
          {buttonContent}
        </ButtonElement>
      );

      return (
        <ButtonWrapper $alignment={buttonAlignment}>
          {buttonLink ? (
            <ButtonLink
              href={buttonLink}
              target={openNewTab ? '_blank' : undefined}
              rel={openNewTab ? 'noopener noreferrer' : undefined}
            >
              {buttonNode}
            </ButtonLink>
          ) : (
            buttonNode
          )}
        </ButtonWrapper>
      );
    }

    if (element.type === 'search_query') {
      if (mergedElement?.searchQueryId && mergedElement?.scope) {
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazySearchQueryRenderer
              searchQueryId={String(mergedElement.searchQueryId)}
              scope={mergedElement.scope}
              searchQueryName={mergedElement.searchQueryName}
              showPagination
            />
          </React.Suspense>
        );
      }
      return (
        <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '6px', textAlign: 'center' }}>
          <StyledText fontSize="14px" color="#666">
            No search query selected. Configure search query in settings.
          </StyledText>
        </div>
      );
    }

    if ((element as any).type === 'public_directory') {
      const pdScope = mergedElement?.scope;
      const pdQueryPayload = mergedElement?.queryPayload || mergedElement?.publicDirectoryPayload;

      if (pdScope) {
        return (
          <React.Suspense fallback={<div>Loading...</div>}>
            <LazyPublicDirectory scope={pdScope} showPagination queryPayload={pdQueryPayload} />
          </React.Suspense>
        );
      }

      return (
        <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '6px', textAlign: 'center' }}>
          <StyledText fontSize="14px" color="#666">
            No directory selected. Configure directory in settings.
          </StyledText>
        </div>
      );
    }

    if (element.type === 'contact_summary' || element.type === 'organization_summary') {
      return (
        <SummaryCardComponent
          summaryType={element.type}
          onProfileClick={() => { }}
        />
      );
    }

    return null;
  };

  return (
    <ElementWrapper className={`${typeClass} ${uniqueElementClass}`} data-element-id={element.id} data-element-type={element.type}>
      {renderElementContent()}
    </ElementWrapper>
  );
};

export default PreviewElement;
